<template>
  <v-dialog :value="internalValue" width="1000px" persistent @input="$emit('input', false)">
    <v-card v-if="internalValue" class="dialog-content">
      <v-card-title>
        <div class="dialog-header d-flex flex-column">
          <span class="dialog-title">Editando o cliente </span>
          <div class="dialog-subtitle d-flex flex-row">
            <div class="subtitle-text d-flex flex-row">
              <span>CPF:</span>
              <span> {{ lazyUser.document_no }}</span>
            </div>
            <div class="subtitle-text d-flex flex-row">
              <span>Origem:</span>
              <span> {{ formatString(lazyUser.origin) }}</span>
            </div>
          </div>
        </div>
      </v-card-title>
      <v-card-text class="scrollable-content">
        <div>
          <v-row>
            <span class="form-label px-3 pb-3">Dados Pessoais</span>
          </v-row>
          <v-row>
            <v-col class="px-3 py-0" cols="4">
              <v-text-field
                v-model="lazyUser.name"
                label="Nome"
                autofocus
                :error-messages="errorMessages.name"
                outlined
                data-cy="userName"
                @keypress="(evt) => inputOnlyRegex(/[A-zÀ-ÿ\- ]/, evt)"
              />
            </v-col>

            <v-col class="px-3 py-0" cols="4">
              <v-text-field
                v-model="lazyUser.phone"
                v-mask="['+55 (##) #####-####', '+55 (##) ####-####']"
                label="Telefone"
                data-cy="userPhone"
                :error-messages="errorMessages.phone"
                outlined
              />
            </v-col>

            <v-col class="px-3 py-0" cols="4">
              <v-text-field v-model="lazyUser.email" label="E-mail" :error-messages="errorMessages.email" outlined @keydown="waitAndValidateEmail">
                <template #append>
                  <mf-icon :icon="emailIcon" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row v-if="isNatural">
            <v-col class="px-3 py-0" cols="4">
              <v-text-field v-model="birth_date" v-mask="'##/##/####'" label="Data de Nascimento" :error-messages="errorMessages.birth_date" outlined />
            </v-col>
            <v-spacer />
            <v-col class="px-3 py-0" cols="8">
              <label class="detail-title">Gênero</label>
              <v-radio-group v-model="lazyUser.gender" row class="radio-list" :error-messages="errorMessages.gender">
                <v-radio label="Masculino" value="M" />
                <v-radio label="Feminino" value="F" />
                <v-radio label="Não identificado" value="U" />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col class="px-3 py-0" cols="4">
              <v-text-field v-model="lazyUser.state_registration" label="Incrição Estadual" :error-messages="errorMessages.state_registration" outlined />
            </v-col>
          </v-row>
          <v-row>
            <span class="form-label px-3 pb-3">Endereço</span>
          </v-row>
          <v-row>
            <v-col class="px-3 py-0" cols="4">
              <v-text-field
                v-model="internalPostalCode"
                v-mask="'#####-###'"
                label="CEP"
                :loading="loadingCep"
                :error-messages="errorMessages.postal_code"
                outlined
              />
            </v-col>
            <v-col class="px-3 py-0" cols="4">
              <v-select
                v-model="address.federative_unit"
                label="Estado"
                :items="estados"
                item-text="name"
                item-value="value"
                :error-messages="errorMessages.federative_unit"
                outlined
                @change="fillAddressCityName(false)"
              />
            </v-col>
            <v-col class="px-3 py-0" cols="4">
              <v-select
                v-model="address.city"
                label="Cidade"
                :disabled="address.federative_unit === ''"
                :items="cidadesFiltradas"
                item-text="Nome"
                item-value="Codigo"
                :error-messages="errorMessages.city"
                outlined
                @change="fillAddressCityName(address.city)"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="px-3 py-0" cols="3">
              <v-text-field v-model="address.neighborhood" label="Bairro" :error-messages="errorMessages.neighborhood" outlined />
            </v-col>
            <v-col class="px-3 py-0" cols="6">
              <v-text-field v-model="address.street" label="Rua" :error-messages="errorMessages.street" outlined />
            </v-col>
            <v-col class="px-3 py-0" cols="3">
              <v-text-field v-model="address.number" label="Número" :error-messages="errorMessages.number" outlined />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="px-3 py-0" cols="6">
              <v-text-field v-model="address.complement" label="Complemento" :error-messages="errorMessages.complement" outlined />
            </v-col>
          </v-row>
          <div v-if="isDynamicAttributes">
            <v-row>
              <span class="form-label px-3 pb-3">Atributos Dinâmicos</span>
            </v-row>
            <v-row>
              <v-col v-for="attribute in userExtraDataDynamicAttributes" :key="attribute._id" class="pt-0" cols="4">
                <v-select
                  v-if="attribute.type === 'OPTIONS'"
                  v-model="lazyUser.extra_data[attribute.attribute]"
                  :items="attribute.options"
                  item-text="label"
                  item-value="value"
                  :label="attribute.label"
                  outlined
                />
                <v-checkbox v-else-if="attribute.type === 'BOOLEAN'" v-model="lazyUser.extra_data[attribute.attribute]" :label="attribute.label" outlined />
              </v-col>
            </v-row>
          </div>
          <div v-if="userTagsByType.length">
            <v-row>
              <v-col v-for="tagOfType in userTagsByType" :key="tagOfType.type" class="pt-0" cols="4">
                <v-row no-gutters>
                  <span class="form-label">{{ tagOfType.label }}</span>
                </v-row>
                <v-row no-gutters class="pt-3">
                  <v-col>
                    <v-chip v-for="tag in tagOfType.tags" :key="`${tagOfType.type}-${tag}`" small>
                      {{ tag }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <mf-action-buttons
          :primary-button="{
            text: 'Salvar',
            action: save,
            isVisible: true,
            isLoading: saving,
            isDisabled: loadingEmail,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: cancel,
            isVisible: true,
            isDisabled: saving,
          }"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import Swal from 'sweetalert2'
import { getDataFromViaCep } from '@/helpers/cep'
import { required, minLength, maxLength, email } from 'vuelidate/lib/validators'
import { MUTATION_EDIT_USER, QUERY_CHECK_AVAILABLE_EMAIL } from '@/modules/clientes/gerenciamento/graphql'
import { notOnlySpace, validBirthdate } from '@/helpers/validators.js'
import { data as cidades } from '@/modules/configuracoes/assets/cidades.json'
import { data as estados } from '@/modules/configuracoes/assets/estados.json'
import { mapGetters } from 'vuex'
const DEFAULT_ADDRESS = {
  postal_code: '',
  federative_unit: '',
  city: '',
  city_name: '',
  neighborhood: '',
  street: '',
  number: '',
  complement: '',
  timeout: null,
}

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: () => {},
    },
    accountConfiguration: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    lazyValue: false,
    lazyUser: {},
    saving: false,
    birth_date: '',
    cidades: cidades,
    estados: estados,
    postalCodeTouched: false,
    loadingCep: false,
    loadingEmail: false,
    availableEmail: true,
    address: DEFAULT_ADDRESS,
  }),
  computed: {
    ...mapGetters(['dynamicAttributes']),
    isDynamicAttributes() {
      return this.$getAllowedModules()['dynamic_attributes'] && this.userExtraDataDynamicAttributes?.length > 0
    },
    userExtraDataDynamicAttributes() {
      return this.dynamicAttributes.filter((attribute) => attribute.origin === 'USER_EXTRA_DATA')
    },
    userTagsByType() {
      const userTagsByType = []
      if (this.lazyUser.signup_tag) {
        userTagsByType.push({
          type: 'signup',
          label: 'Tag de cadastro',
          tags: [this.lazyUser.signup_tag],
        })
      }
      if (this.lazyUser.login_tags?.length) {
        userTagsByType.push({
          type: 'login',
          label: 'Tag de login',
          tags: this.lazyUser.login_tags,
        })
      }
      if (this.lazyUser.update_tags?.length) {
        userTagsByType.push({
          type: 'update',
          label: 'Tag de atualização',
          tags: this.lazyUser.update_tags,
        })
      }
      return userTagsByType
    },
    isNatural() {
      return this.lazyUser?.document_no.length === 11
    },
    emailIcon() {
      if (!this.lazyUser?.email) return ''
      if (this.loadingEmail) return 'sync'
      if (this.availableEmail) return 'done'
      return ''
    },
    internalPostalCode: {
      get() {
        return this.address.postal_code
      },
      set(value) {
        const newRaw = value.replace(/[^\d]+/g, '')
        const oldRaw = this.address.postal_code?.replace(/[^\d]+/g, '')
        if (newRaw !== oldRaw) {
          this.postalCodeTouched = true
        }
        this.address.postal_code = value
      },
    },
    internalValue: {
      get() {
        return this.lazyValue
      },
      set(value) {
        this.lazyValue = value
        this.$emit('input', value)
      },
    },
    cidadesFiltradas() {
      return this.cidades.filter((cidade) => cidade.Uf === this.address.federative_unit)
    },
    errorMessages() {
      const fields = ['name', 'email', 'birth_date', 'gender', 'state_registration', 'address']
      const errors = fields.reduce((errors, field) => {
        if (this.$v.lazyUser[field] && this.$v.lazyUser[field].$error) {
          if (this.$v.lazyUser[field].required === false) errors[field] = 'Campo obrigatório.'
          else if (this.$v.lazyUser[field].notOnlySpace === false) errors[field] = 'Não é permitido salvar apenas espaços.'
        } else {
          errors[field] = ''
        }
        return errors
      }, {})

      if (this.$v.birth_date.$error) errors['birth_date'] = 'Data de nascimento inválida.'
      if (!this.availableEmail && !this.loadingEmail) errors['email'] = 'E-mail em uso por outro usuário.'
      if (this.$v.lazyUser.email.$error) errors['email'] = 'E-mail inválido.'
      if (this.$v.lazyUser.phone.minLength === false || this.$v.lazyUser.phone.maxLength === false) errors.phone = 'Número inválido.'
      if (this.$v.address.postal_code.$error) errors['postal_code'] = 'CEP inválido.'
      if (this.$v.address.federative_unit.$error) errors['federative_unit'] = 'Estado inválido.'
      if (this.$v.lazyUser.state_registration.$error) errors['state_registration'] = 'Inscrição estadual inválida.'
      if (this.$v.address.city.$error && !this.$v.address.federative_unit.$error) errors['city'] = 'Cidade inválida.'
      if (this.$v.address.city_name.$error && !this.$v.address.federative_unit.$error) errors['city_name'] = 'Cidade inválida.'
      if (this.$v.address.neighborhood.$error) errors['neighborhood'] = 'Bairro inválido.'
      if (this.$v.address.street.$error) errors['street'] = 'Rua inválida.'
      if (this.$v.address.number.$error) errors['number'] = 'Número inválido.'
      if (this.$v.address.complement.$error) errors['complement'] = 'Complemento inválido.'
      return errors
    },
  },
  watch: {
    value(value) {
      this.lazyValue = value
      // Reset field
      if (value) {
        this.setupUser(this.user)
      }
    },
    user(value) {
      this.setupUser(value)
    },
    async 'address.postal_code'(value) {
      if (!value) {
        return
      }
      const raw = value.replace(/[^\d]+/g, '')
      if (raw.length !== 8 || !this.postalCodeTouched) {
        return
      }
      try {
        this.loadingCep = true
        const { uf, localidade, ibge, bairro, logradouro } = await getDataFromViaCep(raw)
        this.address.federative_unit = uf || ''
        this.address.city_name = localidade || ''
        this.address.city = Number(ibge) || ''
        this.address.neighborhood = bairro || ''
        this.address.street = logradouro || ''
      } catch (e) {
        this.$snackbar({ message: 'Falha ao procurar o CEP' })
      }
      this.loadingCep = false
    },
  },
  mounted() {
    this.lazyValue = this.value
    this.setupUser(this.user)
  },
  methods: {
    isRequired(fieldName) {
      const fields = this.accountConfiguration?.fields?.[this.isNatural ? 'natural' : 'legal']
      if (fields) {
        let requiredFields = ['address'] // address field is always required
        fields.map((item) => {
          if (item?.validators?.required) {
            requiredFields.push(item.name)
          }
        })
        return requiredFields.includes(fieldName)
      } else return false
    },

    formatString(string) {
      return string ? string[0].toUpperCase().concat(string.substring(1)) : '-'
    },
    setupUser(user) {
      this.postalCodeTouched = false
      this.lazyUser = cloneDeep(user)
      if (!this.lazyUser?.extra_data) {
        this.lazyUser.extra_data = {}
      }
      if (this.lazyUser?.birth_date) {
        const birth_date = this.lazyUser.birth_date.split('-').reverse()
        this.birth_date = birth_date.join('/')
      }
      if (this.lazyUser?.address) {
        this.address = this.lazyUser.address
      } else {
        this.address = cloneDeep(DEFAULT_ADDRESS)
      }
      this.$v.$touch()
    },
    waitAndValidateEmail() {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.validateEmail()
      }, 300)
    },
    getExtraDataForSave() {
      const dynamicAttributeKeys = this.dynamicAttributes.map((attribute) => attribute.attribute)
      return Object.entries(this.lazyUser.extra_data).reduce((acc, [key, value]) => {
        if (dynamicAttributeKeys.includes(key)) acc[key] = value
        return acc
      }, {})
    },
    async validateEmail() {
      // Original email is already unique
      if (!this.lazyUser.email || this.user.email === this.lazyUser.email) {
        this.availableEmail = true
        return
      }
      if (this.$v.lazyUser.email.$error) {
        this.availableEmail = false
        return
      }
      this.loadingEmail = true
      this.availableEmail = false
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_CHECK_AVAILABLE_EMAIL,
          variables: {
            email: this.lazyUser.email,
          },
          fetchPolicy: 'no-cache',
        })
        if (data.checkAvailableUserEmail) {
          this.availableEmail = true
        } else {
          this.$snackbar({ message: 'E-mail já está sendo usado por outro usuário', snackbarColor: 'error' })
        }
      } catch (e) {
        this.$snackbar({ message: 'Falha ao validar e-mail', snackbarColor: 'error' })
      }
      this.loadingEmail = false
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error || !this.availableEmail) {
        return this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
      }
      const confirmed = await this.$confirm({
        title: 'Salvar alterações',
        message:
          'Todas as alterações serão disponibilizadas no aplicativo imediatamente, e na plataforma após a próxima atualização.  Deseja realmente fazer essa alteração?',
        confirmColor: 'primary',
        confirmText: 'Salvar',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.saving = true
      this.lazyUser.address = cloneDeep(this.address)
      this.lazyUser.address.postal_code = this.lazyUser.address.postal_code.replace(/[^\d]+/g, '')
      if (this.birth_date) {
        this.lazyUser.birth_date = this.birth_date.split('/').reverse().join('-')
      }
      const extraData = this.getExtraDataForSave()
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_EDIT_USER,
          variables: {
            user: {
              _id: this.lazyUser._id,
              person_type: this.lazyUser.person_type,
              name: this.lazyUser.name,
              gender: this.lazyUser.gender,
              birth_date: this.lazyUser.birth_date,
              phone: this.lazyUser.phone.replace(/[^0-9]/g, ''),
              state_registration: this.lazyUser.state_registration,
              email: this.lazyUser.email,
              address: {
                id: this.lazyUser.address.id,
                postal_code: this.lazyUser.address.postal_code,
                street: this.lazyUser.address.street,
                neighborhood: this.lazyUser.address.neighborhood,
                number: this.lazyUser.address.number,
                complement: this.lazyUser.address.complement,
                city: this.lazyUser.address.city,
                city_name: this.lazyUser.address.city_name,
                federative_unit: this.lazyUser.address.federative_unit,
              },
              ...(extraData && Object.keys(extraData).length ? { extra_data: extraData } : {}),
            },
          },
        })
        Swal.fire({
          text: 'Cadastro editado',
          icon: 'success',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
        this.internalValue = false
        this.$emit('save')
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao editar o cadastro' })
      } finally {
        this.loading = false
        this.saving = false
      }
    },
    cancel() {
      this.internalValue = false
      this.lazyUser = {}
      this.$emit('close')
    },
    fillAddressCityName(IBGECityCode) {
      if (IBGECityCode) {
        const city = this.cidades.find(({ Codigo }) => Codigo === IBGECityCode)
        this.address.city_name = city?.Nome ?? ''
      } else {
        this.address.city = ''
        this.address.city_name = ''
      }
    },
  },
  validations() {
    return {
      lazyUser: {
        name: {
          notOnlySpace,
          ...(this.isRequired('name')
            ? {
                required,
              }
            : {}),
        },
        email: {
          email,
          ...(this.isRequired('email')
            ? {
                required,
              }
            : {}),
        },
        phone: {
          notOnlySpace,
          minLength: minLength(18),
          maxLength: maxLength(19),
          ...(this.isRequired('phone')
            ? {
                required,
              }
            : {}),
        },
        state_registration: {
          notOnlySpace,
          ...(this.isRequired('state_registration')
            ? {
                required,
              }
            : {}),
        },
      },
      birth_date: {
        valid: validBirthdate,
        ...(this.isRequired('birth_date')
          ? {
              required,
            }
          : {}),
      },
      address: {
        postal_code: {
          notOnlySpace,
          minLength: minLength(9),
          maxLength: maxLength(9),
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        federative_unit: {
          notOnlySpace,
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        city: {
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        city_name: {
          notOnlySpace,
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        neighborhood: {
          notOnlySpace,
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        street: {
          notOnlySpace,
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        number: {
          notOnlySpace,
          ...(this.isRequired('address')
            ? {
                required,
              }
            : {}),
        },
        complement: { notOnlySpace },
      },
    }
  },
}
</script>

<style lang="scss">
.dialog-content {
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  .v-card__title {
    flex-shrink: 0;
  }

  .v-card__text {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0;
  }

  .v-card__actions {
    flex-shrink: 0;
  }

  .dialog-header {
    .dialog-title {
      font-size: 20px;
      font-weight: 700;
      line-height: 120%; /* 24px */
      color: $cinza-d10;
      margin-bottom: 6px;
    }
    .dialog-subtitle {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      gap: 20px;
      :first-child {
        color: $cinza-d10;
        margin-right: 2px;
      }
      :nth-child(2) {
        color: $cinza-0;
      }
    }
  }
  .detail-title {
    opacity: 0.8;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.2;
    letter-spacing: 0.24px;
    color: $preto;
    margin-bottom: 5px;
  }
  .radio-list {
    margin-top: 4px !important;
  }
  .form-label {
    color: $cinza-0;
    font-size: 15px;
    font-weight: 550;
  }
}
.flag {
  display: block;
  height: 30px;
}
</style>
